
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useGetProductsWithDetails } from "@/hooks/use-products-with-details";
import { ProcurementFormValues } from "../schema";

interface ProductDropdownProps {
  form: UseFormReturn<ProcurementFormValues>;
  index: number;
  label?: string;
  placeholder?: string;
}

export const ProductDropdown = ({ 
  form, 
  index, 
  label = "Product Name",
  placeholder = "Select a product..."
}: ProductDropdownProps) => {
  const [open, setOpen] = useState(false);
  const { data: products = [], isLoading } = useGetProductsWithDetails();
  
  const currentProductId = form.watch(`items.${index}.product_id`);
  const currentProduct = products.find(p => p.id === currentProductId);

  const handleProductSelect = (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      form.setValue(`items.${index}.product_id`, product.id);
      form.setValue(`items.${index}.product_name`, product.name);
      form.setValue(`items.${index}.unit_of_measure`, product.unit_of_measure || 'kg');
      form.trigger(`items.${index}`);
    }
    setOpen(false);
  };

  return (
    <FormField
      control={form.control}
      name={`items.${index}.product_id`}
      render={({ field }) => (
        <FormItem className="flex flex-col">
          <FormLabel>{label}</FormLabel>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className={cn(
                    "justify-between",
                    !field.value && "text-muted-foreground"
                  )}
                >
                  {currentProduct?.name || placeholder}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command>
                <CommandInput placeholder="Search products..." />
                <CommandList>
                  <CommandEmpty>
                    {isLoading ? "Loading products..." : "No products found."}
                  </CommandEmpty>
                  <CommandGroup>
                    {products.map((product) => (
                      <CommandItem
                        key={product.id}
                        value={product.name}
                        onSelect={() => handleProductSelect(product.id)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            field.value === product.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {product.name}
                        {product.unit_of_measure && product.unit_of_measure !== 'kg' && (
                          <span className="ml-1 text-muted-foreground">
                            ({product.unit_of_measure})
                          </span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
